import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';

import { actionCloseShiftHandover } from 'lib/actions';
import { getShiftHandoverOpen } from 'lib/selectors/getShiftHandoverOpen';
import {
  MMEmbeddableZone,
  useEmbeddedEffect,
} from '@machinemetrics/mm-react-embeddable';
import { SHIFT_HANDOVER_HOST } from 'lib/appConfig';
import i18n from 'i18next';
import { getClockOutOperator } from 'lib/selectors/getClockOutOperator';
import { getLatestOperator } from 'lib/selectors/getLatestOperator';
import { SET_TIMESHEET_CLOCK_OUT } from 'lib/api/mutations';
import PersistentDrawer, {
  PersistentDrawerBody,
  PersistentDrawerHeader,
  PersistentDrawerCloseButton,
} from '../PersistentDrawer';

const EmbeddedShiftHandover = ({ id, operator }) => {
  const dispatch = useDispatch();
  const [path, setPath] = useState('/handover');

  const [setTimesheetClockOut] = useMutation(SET_TIMESHEET_CLOCK_OUT, {
    onCompleted: () => {
      // Timesheet clockOut set successfully
    },
    onError: () => {
      // Error setting timesheet clockOut
    },
  });

  useEmbeddedEffect(id, 'shift-handover:close', () => {
    dispatch(actionCloseShiftHandover());
  });

  useEmbeddedEffect(id, 'set-path', (data) => {
    if (data?.path) {
      setPath(data.path);
    }
  });

  useEmbeddedEffect(id, 'shift-handover:handle-submit', async () => {
    const personId = operator?.erpId;

    // Set clockOut on the active timesheet (where clockOut is null) for this personId
    if (personId) {
      try {
        await setTimesheetClockOut({
          variables: {
            personId,
            clockOut: new Date().toISOString(),
          },
        });
      } catch (error) {
        // Failed to set timesheet clockOut
      }
    }

    dispatch(actionCloseShiftHandover());
  });

  const context = {
    operatorId: operator?.id, // Internal operator ID
    operatorErpId: operator?.erpId, // ERP ID (personId from labor tickets)
    operatorName: operator?.name,
  };

  return (
    <MMEmbeddableZone
      id={id}
      src={`${SHIFT_HANDOVER_HOST}`}
      path={path}
      context={context}
      allow="microphone"
      params={{
        language: i18n.language,
      }}
    />
  );
};

export const ShiftHandoverWrapper = () => {
  const id = 'shift-handover';
  const dispatch = useDispatch();
  const shiftHandoverOpen = useSelector(getShiftHandoverOpen);
  const clockOutOperator = useSelector(getClockOutOperator);
  const latestOperator = useSelector(getLatestOperator);

  // clockOutOperator is the erpId
  const operatorErpId = clockOutOperator || latestOperator?.erpId;

  // Build operator object with both internal id, erpId and name
  const operator = {
    id: latestOperator?.id, // Internal operator ID
    erpId: operatorErpId, // ERP ID (personId)
    name: latestOperator?.name || operatorErpId, // Fallback to erpId if name not available
  };

  return (
    <PersistentDrawer
      isOpen={shiftHandoverOpen}
      onClose={() => {
        return dispatch(actionCloseShiftHandover());
      }}
      placement="bottom"
      size="full"
    >
      <PersistentDrawerHeader>
        Shift Handover
        <PersistentDrawerCloseButton />
      </PersistentDrawerHeader>
      <PersistentDrawerBody
        style={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          margin: 0,
        }}
      >
        <EmbeddedShiftHandover id={id} operator={operator} />
      </PersistentDrawerBody>
    </PersistentDrawer>
  );
};
